import { useState, useEffect, useCallback, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { extractExif, type ExifData } from '../../lib/exifExtract'
import LoadingDots from '../LoadingDots'

/**
 * Prepares a file for staging.
 * HEIC/HEIF files are converted to JPEG so that:
 *  1. The preview <img> can render them (browsers can't decode HEIC natively on desktop)
 *  2. The converted JPEG is what gets uploaded — no second conversion needed later
 *
 * heic-to uses magic-byte detection so it works even when file.type is ""
 * (common on desktop Chrome for HEIC files).
 *
 * Returns the upload-ready File and a blob URL for the preview.
 */
async function prepareFile(file: File): Promise<{ uploadFile: File; previewUrl: string }> {
  try {
    const { isHeic, heicTo } = await import('heic-to')
    if (await isHeic(file)) {
      const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.82 })
      const name = file.name.replace(/\.[^.]+$/, '.jpg')
      const uploadFile = new File([blob], name, { type: 'image/jpeg' })
      return { uploadFile, previewUrl: URL.createObjectURL(uploadFile) }
    }
  } catch (err) {
    console.error('HEIC conversion failed:', err)
  }
  return { uploadFile: file, previewUrl: URL.createObjectURL(file) }
}

interface StagedFile {
  /** Stable key used by dnd-kit — never changes even after reorder */
  id: string
  /** Original file — used for EXIF extraction */
  file: File
  /** Upload-ready file — HEIC converted to JPEG, otherwise same as file */
  uploadFile: File
  previewUrl: string
  /** EXIF createdAt (ms) used for default sort; falls back to File.lastModified */
  createdAtMs: number
  /** Pre-extracted EXIF metadata from the original file */
  exif: ExifData
}

interface SortableItemProps {
  staged: StagedFile
  onRemove: () => void
}

function SkeletonTile() {
  return (
    <div className="aspect-square rounded bg-gray-900 overflow-hidden relative">
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/5 to-transparent" />
    </div>
  )
}

function SortableItem({ staged, onRemove }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: staged.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: 'none',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square group"
    >
      {/* Drag handle covers the whole tile */}
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing"
      />
      {staged.file.type.startsWith('video/') ? (
        <>
          <video
            src={staged.previewUrl}
            className="w-full h-full object-cover rounded"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => { e.currentTarget.currentTime = 0.001 }}
          />
          {/* Play icon overlay — visible until the browser paints a poster frame */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black bg-opacity-40 rounded-full p-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24" className="w-6 h-6">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </>
      ) : (
        <img
          src={staged.previewUrl}
          alt=""
          className="w-full h-full object-cover rounded"
        />
      )}
      {/* Date overlay — bottom-left corner */}
      {staged.createdAtMs > 0 && !isNaN(staged.createdAtMs) && (
        <div className="absolute bottom-1 left-1 z-20 bg-black bg-opacity-60 text-white text-[10px] leading-tight px-1.5 py-0.5 rounded pointer-events-none">
          {new Date(staged.createdAtMs).toLocaleDateString('nb-NO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </div>
      )}
      {/* Remove button — sits above the drag handle */}
      <button
        onClick={onRemove}
        className="
          absolute top-1 right-1 z-20
          w-7 h-7
          bg-black bg-opacity-60
          text-white rounded-full
          flex items-center justify-center
          hover:bg-opacity-80
          transition-opacity duration-200
        "
        aria-label="Fjern"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

interface FileWithExif {
  file: File
  exif: ExifData
}

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  onUpload: (files: FileWithExif[], username: string, title: string, collectionDate?: Date) => Promise<void>
  /** When true, shows the date picker on the Tittel step */
  showDatePicker?: boolean
}

const STEPS = ['Bilder', 'Tittel', 'Navn'] as const
type Step = 0 | 1 | 2

function nowLocalDateTimeString() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`
}

function latestDateString(files: StagedFile[]): string {
  if (files.length === 0) return nowLocalDateTimeString()
  const maxMs = Math.max(...files.map(f => f.createdAtMs))
  const d = new Date(maxMs)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`
}



export default function UploadModal({ isOpen, onClose, onUpload, showDatePicker = false }: UploadModalProps) {
  const [step, setStep] = useState<Step>(0)
  const [username, setUsername] = useState('')
  const [title, setTitle] = useState('')
  const [collectionDateStr, setCollectionDateStr] = useState(nowLocalDateTimeString)
  const hasUserEditedDate = useRef(false)
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingCount, setProcessingCount] = useState(0)
  const [showLimitWarning, setShowLimitWarning] = useState(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  )

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Revoke all preview URLs when the modal closes or component unmounts
  useEffect(() => {
    if (!isOpen) {
      stagedFiles.forEach((sf) => URL.revokeObjectURL(sf.previewUrl))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Suggest the latest EXIF date from staged files, but only if the
  // user hasn't manually edited the date picker.
  useEffect(() => {
    if (!hasUserEditedDate.current) {
      setCollectionDateStr(latestDateString(stagedFiles))
    }
  }, [stagedFiles])

  const addFiles = useCallback(async (newFiles: File[]) => {
    const remaining = 9 - stagedFiles.length
    if (remaining <= 0) return

    if (newFiles.length > remaining) {
      setShowLimitWarning(true)
      setTimeout(() => setShowLimitWarning(false), 4000)
    }

    const filesToAdd = newFiles.slice(0, remaining)

    // Deduplicate against already-staged files by name + size
    const existingKeys = new Set(stagedFiles.map((sf) => `${sf.file.name}|${sf.file.size}`))
    const unique = filesToAdd.filter((f) => !existingKeys.has(`${f.name}|${f.size}`))
    if (unique.length === 0) return

    setProcessingCount(unique.length)
    setIsProcessing(true)

    // Extract EXIF and convert HEIC files before staging
    const staged: StagedFile[] = await Promise.all(
      unique.map(async (file) => {
        // EXIF must be extracted from the original file before any conversion
        const exif = await extractExif(file)
        const createdAtMs = exif.createdAt
          ? new Date(exif.createdAt).getTime()
          : file.lastModified
        const { uploadFile, previewUrl } = await prepareFile(file)
        return {
          id: crypto.randomUUID(),
          file,
          uploadFile,
          previewUrl,
          createdAtMs,
          exif,
        }
      })
    )

    setStagedFiles((prev) => {
      const combined = [...prev, ...staged]
      // Sort by EXIF/lastModified date descending (newest first) — this is the
      // default order. Once the user drags to reorder, that order takes over.
      combined.sort((a, b) => b.createdAtMs - a.createdAtMs)
      return combined
    })
    setIsProcessing(false)
  }, [stagedFiles.length])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    // Reset input so the same files can be re-selected if needed
    e.target.value = ''
    void addFiles(files)
  }

  const removeFile = (id: string) => {
    setStagedFiles((prev) => {
      const sf = prev.find((f) => f.id === id)
      if (sf) URL.revokeObjectURL(sf.previewUrl)
      return prev.filter((f) => f.id !== id)
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setStagedFiles((prev) => {
        const oldIndex = prev.findIndex((f) => f.id === active.id)
        const newIndex = prev.findIndex((f) => f.id === over.id)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  const handleUpload = async () => {
    if (!username.trim() || stagedFiles.length === 0) return

    setIsUploading(true)
    try {
      // Build collectionDate from the datetime-local string when the picker is shown.
      // The input value is a local datetime string (e.g. "2026-07-27T14:30"), parsed
      // directly as a local Date so timezone offsets don't shift the value.
      let collectionDate: Date | undefined
      if (showDatePicker && collectionDateStr) {
        collectionDate = new Date(collectionDateStr)
      }

      // Pass files in their current (possibly reordered) sequence.
      // The index in this array becomes the item's position in the DB.
      await onUpload(
        stagedFiles.map((sf) => ({ file: sf.uploadFile, exif: sf.exif })),
        username.trim(),
        title.trim(),
        collectionDate,
      )
      // Revoke preview URLs before clearing state
      stagedFiles.forEach((sf) => URL.revokeObjectURL(sf.previewUrl))
      setUsername('')
      setTitle('')
      setCollectionDateStr(nowLocalDateTimeString())
      hasUserEditedDate.current = false
      setStagedFiles([])
      setIsProcessing(false)
      setStep(0)
      onClose()
    } catch (error) {
      console.error('Upload failed:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      stagedFiles.forEach((sf) => URL.revokeObjectURL(sf.previewUrl))
      setUsername('')
      setTitle('')
      setCollectionDateStr(nowLocalDateTimeString())
      hasUserEditedDate.current = false
      setStagedFiles([])
      setIsProcessing(false)
      setStep(0)
      onClose()
    }
  }

  const canAdvance =
    step === 0 ? stagedFiles.length > 0 :
    step === 1 ? true :  // title is optional — always allow advancing
    username.trim().length > 0

  const goNext = () => {
    if (step < 2) setStep((step + 1) as Step)
    else void handleUpload()
  }

  const goBack = () => {
    if (step > 0) setStep((step - 1) as Step)
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
    >
      <div className="w-full max-w-lg bg-gray-900 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header with progress dots */}
        <div className="relative flex items-center justify-center gap-2 pt-8 pb-6 flex-shrink-0">
          {step > 0 && (
            <button
              type="button"
              onClick={goBack}
              disabled={isUploading}
              className="absolute left-8 text-gray-500 hover:text-white transition-colors p-1 rounded disabled:opacity-40"
              aria-label="Tilbake"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === step
                  ? 'w-6 h-2 bg-blue-500'
                  : i < step
                  ? 'w-2 h-2 bg-blue-400'
                  : 'w-2 h-2 bg-gray-600'
              }`}
            />
          ))}
          <button
            type="button"
            onClick={handleClose}
            disabled={isUploading}
            className="absolute right-8 text-gray-500 hover:text-white transition-colors p-1 rounded disabled:opacity-40"
            aria-label="Lukk"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Warning Toast */}
        {showLimitWarning && (
          <div className="mx-8 mb-4 rounded-lg bg-gray-700 text-white px-4 py-3 text-sm text-center flex-shrink-0">
            Kun de 9 første bildene ble valgt
          </div>
        )}

        {/* Step body */}
        <div className="flex flex-col gap-6 px-8 pb-8 overflow-y-auto">

          {/* Step 0 — Bilder */}
          {step === 0 && (
            <div className="flex flex-col gap-3">
              {isProcessing && stagedFiles.length === 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">Behandler {processingCount} filer...</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: processingCount }).map((_, i) => (
                      <SkeletonTile key={i} />
                    ))}
                  </div>
                </div>
              ) : stagedFiles.length === 0 ? (
                <label className="block w-full rounded-xl border-2 border-dashed border-gray-600 hover:border-gray-400 p-12 text-center cursor-pointer transition-colors">
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime"
                    multiple
                    className="hidden"
                  />
                  <svg
                    className="mx-auto h-12 w-12 text-gray-500 mb-3"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-gray-400 font-medium mb-1">Velg bilder eller videoer</p>
                  <p className="text-sm text-gray-600">Maks 9 filer</p>
                </label>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">{stagedFiles.length} av 9</span>
                    {stagedFiles.length < 9 && (
                      <label className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer font-medium transition-colors">
                        + Legg til flere
                        <input
                          type="file"
                          onChange={handleFileSelect}
                          accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime"
                          multiple
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  {!isProcessing && stagedFiles.length > 1 && (
                    <p className="text-xs text-gray-600 mb-2 text-center">Dra for å endre rekkefølge</p>
                  )}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={stagedFiles.map((sf) => sf.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-3 gap-2">
                        {stagedFiles.map((sf) => (
                          <SortableItem
                            key={sf.id}
                            staged={sf}
                            onRemove={() => removeFile(sf.id)}
                          />
                        ))}
                        {isProcessing && (
                          <p className="col-span-3 text-xs text-gray-500 text-center pt-1">Behandler {processingCount} filer...</p>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {isProcessing && (
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: processingCount }).map((_, i) => (
                        <SkeletonTile key={`skel-${i}`} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 1 — Tittel */}
          {step === 1 && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-gray-400 text-sm font-medium">Tittel <span className="text-gray-600">(valgfritt)</span></label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') goNext() }}
                  placeholder="Søndagstur"
                  maxLength={100}
                  autoFocus
                  className="bg-gray-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                />
              </div>
              {showDatePicker && (
                <div className="flex flex-col gap-1">
                  <label className="text-gray-400 text-sm font-medium">Dato og tid</label>
                  <input
                    type="datetime-local"
                    value={collectionDateStr}
                    max={nowLocalDateTimeString()}
                    onChange={(e) => { setCollectionDateStr(e.target.value); hasUserEditedDate.current = true }}
                    className="bg-gray-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Navn */}
          {step === 2 && (
            <div className="flex flex-col gap-1">
              <label className="text-gray-400 text-sm font-medium">Ditt navn</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canAdvance) goNext() }}
                placeholder="F.eks. Ola Nordmann"
                maxLength={50}
                autoFocus
                className="bg-gray-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
              />
            </div>
          )}

          {/* Next / Upload button */}
          <button
            type="button"
            onClick={goNext}
            disabled={!canAdvance || isUploading}
            className="
              w-full py-3 rounded-xl
              bg-blue-500 hover:bg-blue-600
              disabled:bg-gray-700 disabled:cursor-not-allowed
              text-white font-semibold
              transition-colors duration-200
              flex items-center justify-center gap-2
            "
          >
            {isUploading && (
              <LoadingDots />
            )}
            {step < 2 ? 'Neste' : isUploading ? 'Laster opp...' : 'Last opp bilder'}
          </button>
        </div>
      </div>
    </div>
  )
}
